import React, { useEffect } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./i18n";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import MentionsLegalesPage from "./pages/MentionsLegalesPage";
import AuthCallback from "./pages/AuthCallback";
import PayPage from "./pages/PayPage";
import KiddoushPage from "./pages/KiddoushPage";
import KiddoushConfirmPage from "./pages/KiddoushConfirmPage";
import DonorPortalPage from "./pages/DonorPortalPage";
import ContactPage from "./pages/ContactPage";
import PublicDonatePage from "./pages/PublicDonatePage";
import CampaignPublicPage from "./pages/CampaignPublicPage";
import FestivalPublicPage from "./pages/FestivalPublicPage";
import AllCampaignsPage from "./pages/AllCampaignsPage";
import AllOrganizationsPage from "./pages/AllOrganizationsPage";
import OrganizationPublicPage from "./pages/OrganizationPublicPage";
import OrganizationContactPage from "./pages/OrganizationContactPage";
import VerifyIdentityPage from "./pages/VerifyIdentityPage";
import { Toaster } from "./components/ui/sonner";
import InstallPrompt from "./components/InstallPrompt";
import ZoneChargement from "./components/shared/ZoneChargement";
// Pages d'équipe (connexion, administration, owner, Poche) : chargées à la
// demande, hors du fichier principal que télécharge chaque donateur — voir
// routes/pagesDifferees.js. Les pages publiques ci-dessus restent importées
// directement : un donateur reçoit sa page en un seul téléchargement.
import {
  CadreAdmin,
  LoginPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  InviteAcceptPage,
  ResponsablePage,
  Dashboard,
  GestionDonsPage,
  DonorsListPage,
  DonorFormPage,
  DonorDetailPage,
  PledgesPage,
  PledgeFormPage,
  PledgeDetailPage,
  CerfasPage,
  PostShabbatPage,
  KiddoushAdminPage,
  CampaignsListPage,
  CampaignNewPage,
  CampaignDetailPage,
  CommunicationPage,
  MessagesPage,
  WhatsAppInboxPage,
  WhatsAppStatsPage,
  SmsStatsPage,
  SecurityPage,
  UsersSettingsPage,
  OrganizationSettingsPage,
  OrganizationSubscriptionPage,
  OwnerDashboard,
  OrganizationsListPage,
  OwnerOrgDashboard,
  OwnerAiUsagePage,
  OwnerConsumptionPage,
  OwnerPlatformSettingsPage,
  OwnerRevenuePage,
  OwnerSubscriptionsPage,
  OwnerStripePage,
  SettingsPage,
  OnboardingPage,
  PocheShell,
  PocheHome,
  PocheDictate,
  PocheVerify,
  PocheFind,
} from "./routes/pagesDifferees";

const ALL_ROLES = ["owner", "admin", "manager"];

// Page d'équipe : garde d'accès (chargée d'office, légère : un visiteur non
// connecté est renvoyé vers /login sans télécharger le code de l'administration),
// puis cadre + page, chargés à la demande. Deux zones de chargement :
//   - autour de tout (groupe « equipe ») : en arrivant d'ailleurs (connexion,
//     page publique), repli pleine page pendant l'arrivée du cadre — jamais
//     l'ancien écran figé (voir ZoneChargement) ;
//   - DANS le cadre : d'une page d'équipe à l'autre, la barre latérale reste.
function Shell({ children, allowedRoles }) {
  return (
    <ZoneChargement pleinePage groupe="equipe">
      <ProtectedRoute allowedRoles={allowedRoles}>
        <CadreAdmin>
          <ZoneChargement>{children}</ZoneChargement>
        </CadreAdmin>
      </ProtectedRoute>
    </ZoneChargement>
  );
}

// Pages d'accès de l'équipe (connexion, mot de passe oublié, invitation, lien
// du responsable) : leur propre zone de chargement, pour que le repli
// s'affiche dès qu'on y arrive depuis une autre page (voir ZoneChargement).
function PageCompte({ children }) {
  return <ZoneChargement pleinePage groupe="compte">{children}</ZoneChargement>;
}

// Terouma Poche : cadre propre sans barre latérale, sa propre zone aussi.
function PagePoche({ children }) {
  return (
    <ZoneChargement pleinePage groupe="poche">
      <PocheShell>{children}</PocheShell>
    </ZoneChargement>
  );
}

// Pages WhatsApp : accessibles uniquement si l'owner a activé WhatsApp pour
// l'org. Sinon redirection — aucune page WhatsApp n'est atteignable (même par
// URL directe) quand WhatsApp est désactivé côté owner.
function WhatsAppOnly({ children }) {
  const { user } = useAuth();
  if (!user?.whatsapp_granted) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();

  // Handle OAuth callback (Emergent puts session_id in URL fragment)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
      <Route path="/login" element={<PageCompte><LoginPage /></PageCompte>} />
      <Route path="/mot-de-passe-oublie" element={<PageCompte><ForgotPasswordPage /></PageCompte>} />
      <Route path="/reinitialiser-mot-de-passe/:token" element={<PageCompte><ResetPasswordPage /></PageCompte>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/pay/:token" element={<PayPage />} />
      <Route path="/verify-identity/:token" element={<VerifyIdentityPage />} />
      <Route path="/kiddoush" element={<KiddoushPage />} />
      <Route path="/kiddoush/confirm/:token" element={<KiddoushConfirmPage />} />
      <Route path="/organisations" element={<AllOrganizationsPage />} />
      <Route path="/organisations/:orgId" element={<OrganizationPublicPage />} />
      <Route path="/organisations/:orgId/contact" element={<OrganizationContactPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/donate/:orgId" element={<PublicDonatePage />} />
      <Route path="/c/:id" element={<CampaignPublicPage />} />
      {/* Page publique d'une fête (Kippour…) — lien diffusé sur WhatsApp */}
      <Route path="/fete/:pageId" element={<FestivalPublicPage />} />
      <Route path="/campagnes" element={<AllCampaignsPage />} />
      <Route path="/mon-espace" element={<DonorPortalPage />} />
      <Route path="/mon-espace/:token" element={<DonorPortalPage />} />
      <Route path="/invite/:token" element={<PageCompte><InviteAcceptPage /></PageCompte>} />
      {/* Responsable de la collecte d'une synagogue : lien Terouma à jeton
          transmis par WhatsApp, qui ouvre l'inscription Stripe (un clic). */}
      <Route path="/responsable/:token" element={<PageCompte><ResponsablePage /></PageCompte>} />

      <Route
        path="/dashboard"
        element={
          <Shell allowedRoles={["admin", "manager"]}>
            <Dashboard />
          </Shell>
        }
      />
      <Route
        path="/owner/dashboard"
        element={
          <Shell allowedRoles={["owner"]}>
            <OwnerDashboard />
          </Shell>
        }
      />
      <Route
        path="/owner/organizations"
        element={
          <Shell allowedRoles={["owner"]}>
            <OrganizationsListPage />
          </Shell>
        }
      />
      <Route
        path="/owner/organizations/:orgId"
        element={
          <Shell allowedRoles={["owner"]}>
            <OwnerOrgDashboard />
          </Shell>
        }
      />
      <Route
        path="/owner/ai-usage"
        element={
          <Shell allowedRoles={["owner"]}>
            <OwnerAiUsagePage />
          </Shell>
        }
      />
      {/* Consommation des messages (SMS / WhatsApp / emails) de toutes les
          synagogues : coûts, quotas des formules, alertes — owner seulement. */}
      <Route
        path="/owner/consumption"
        element={
          <Shell allowedRoles={["owner"]}>
            <OwnerConsumptionPage />
          </Shell>
        }
      />
      <Route
        path="/owner/settings"
        element={
          <Shell allowedRoles={["owner"]}>
            <OwnerPlatformSettingsPage />
          </Shell>
        }
      />
      <Route
        path="/owner/revenue"
        element={
          <Shell allowedRoles={["owner"]}>
            <OwnerRevenuePage />
          </Shell>
        }
      />
      <Route
        path="/settings/security"
        element={
          <Shell allowedRoles={ALL_ROLES}>
            <SecurityPage />
          </Shell>
        }
      />
      <Route
        path="/settings/users"
        element={
          <Shell allowedRoles={["owner", "admin"]}>
            <UsersSettingsPage />
          </Shell>
        }
      />
      <Route
        path="/settings/organization"
        element={
          <Shell allowedRoles={["owner", "admin"]}>
            <OrganizationSettingsPage />
          </Shell>
        }
      />
      <Route
        path="/settings/subscription"
        element={
          <Shell allowedRoles={["owner", "admin", "manager"]}>
            <OrganizationSubscriptionPage />
          </Shell>
        }
      />
      <Route
        path="/owner/subscriptions"
        element={
          <Shell allowedRoles={["owner"]}>
            <OwnerSubscriptionsPage />
          </Shell>
        }
      />
      {/* Paiements des synagogues : comptes Stripe créés par l'owner au nom
          du responsable, lien WhatsApp, suivi des exigences Stripe. */}
      <Route
        path="/owner/paiements"
        element={
          <Shell allowedRoles={["owner"]}>
            <OwnerStripePage />
          </Shell>
        }
      />
      <Route
        path="/post-chabat"
        element={
          <Shell allowedRoles={["admin", "manager"]}>
            <PostShabbatPage />
          </Shell>
        }
      />
      <Route
        path="/kiddoush/manage"
        element={
          <Shell allowedRoles={["admin", "manager"]}>
            <KiddoushAdminPage />
          </Shell>
        }
      />

      {/* Terouma Poche — version téléphone (dictée du soir, retrouver un don).
          Cadre propre sans barre latérale : PagePoche (PocheShell), pas Shell. */}
      <Route path="/poche" element={<PagePoche><PocheHome /></PagePoche>} />
      <Route path="/poche/dicter" element={<PagePoche><PocheDictate /></PagePoche>} />
      <Route path="/poche/verifier" element={<PagePoche><PocheVerify /></PagePoche>} />
      <Route path="/poche/retrouver" element={<PagePoche><PocheFind /></PagePoche>} />

      {/* Communication — hub regroupant SMS, WhatsApp, Boîte WhatsApp, Messages */}
      <Route
        path="/communication"
        element={
          <Shell allowedRoles={["owner", "admin", "manager"]}>
            <CommunicationPage />
          </Shell>
        }
      />

      {/* Messages inbox — all roles (owner sees platform, admin/manager sees their org) */}
      <Route
        path="/messages"
        element={
          <Shell allowedRoles={["owner", "admin", "manager"]}>
            <MessagesPage />
          </Shell>
        }
      />

      {/* WhatsApp inbox — donors who replied on WhatsApp (owner sees all, org-scoped otherwise) */}
      <Route
        path="/whatsapp"
        element={
          <Shell allowedRoles={["owner", "admin", "manager"]}>
            <WhatsAppOnly><WhatsAppInboxPage /></WhatsAppOnly>
          </Shell>
        }
      />

      {/* SMS stats + toggle */}
      <Route
        path="/sms-stats"
        element={
          <Shell allowedRoles={["owner", "admin", "manager"]}>
            <SmsStatsPage />
          </Shell>
        }
      />

      {/* WhatsApp stats + toggle (Twilio per-conversation pricing) */}
      <Route
        path="/whatsapp-stats"
        element={
          <Shell allowedRoles={["owner", "admin", "manager"]}>
            <WhatsAppOnly><WhatsAppStatsPage /></WhatsAppOnly>
          </Shell>
        }
      />

      {/* Campaigns module */}
      <Route
        path="/campaigns"
        element={
          <Shell allowedRoles={["admin", "manager"]}>
            <CampaignsListPage />
          </Shell>
        }
      />
      <Route
        path="/campaigns/new"
        element={
          <Shell allowedRoles={["admin", "manager"]}>
            <CampaignNewPage />
          </Shell>
        }
      />
      <Route
        path="/campaigns/:id"
        element={
          <Shell allowedRoles={["admin", "manager"]}>
            <CampaignDetailPage />
          </Shell>
        }
      />

      {/* Hub « Gestion des dons » — regroupe Donateurs / Engagements / Reçus Cerfa.
          Les routes individuelles ci-dessous restent actives (liens profonds,
          fiches, retour, pré-remplissage Cerfa). */}
      <Route
        path="/gestion-dons"
        element={
          <Shell allowedRoles={ALL_ROLES}>
            <GestionDonsPage />
          </Shell>
        }
      />

      {/* Donors module */}
      <Route
        path="/donors"
        element={
          <Shell allowedRoles={ALL_ROLES}>
            <DonorsListPage />
          </Shell>
        }
      />
      <Route
        path="/donors/new"
        element={
          <Shell allowedRoles={ALL_ROLES}>
            <DonorFormPage />
          </Shell>
        }
      />
      <Route
        path="/donors/:id"
        element={
          <Shell allowedRoles={ALL_ROLES}>
            <DonorDetailPage />
          </Shell>
        }
      />
      <Route
        path="/donors/:id/edit"
        element={
          <Shell allowedRoles={ALL_ROLES}>
            <DonorFormPage />
          </Shell>
        }
      />

      {/* Pledges module */}
      <Route
        path="/pledges"
        element={
          <Shell allowedRoles={ALL_ROLES}>
            <PledgesPage />
          </Shell>
        }
      />
      <Route
        path="/pledges/new"
        element={
          <Shell allowedRoles={ALL_ROLES}>
            <PledgeFormPage />
          </Shell>
        }
      />
      <Route
        path="/pledges/:id"
        element={
          <Shell allowedRoles={ALL_ROLES}>
            <PledgeDetailPage />
          </Shell>
        }
      />

      {/* Pledges placeholder */}
      <Route
        path="/cerfa"
        element={
          <Shell allowedRoles={ALL_ROLES}>
            <CerfasPage />
          </Shell>
        }
      />
      {/* Réservées à l'OWNER (décision d'Avi du 30/09/2026) : la clé Brevo
          et le compte de paiement Stripe de chaque synagogue sont gérés par
          l'équipe Terouma, jamais par un admin/manager. /settings ne fait
          plus que rediriger ; /onboarding/stripe reste le retour de Stripe
          quand l'owner connecte un compte en ayant ouvert la synagogue (le
          parcours du responsable passe par /responsable/:token). */}
      <Route
        path="/settings"
        element={
          <Shell allowedRoles={["owner"]}>
            <SettingsPage />
          </Shell>
        }
      />
      <Route
        path="/onboarding/stripe"
        element={
          <Shell allowedRoles={["owner"]}>
            <OnboardingPage />
          </Shell>
        }
      />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  // Strip the "Made with Emergent" watermark. It uses inline !important styles
  // that CSS cannot override, so we remove the node and keep watching for
  // re-insertion.
  useEffect(() => {
    const strip = () => {
      const el = document.getElementById("emergent-badge");
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
    strip();
    const observer = new MutationObserver(strip);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Wake-up ping pour Railway: au chargement de l'app, on ping silencieusement
  // l'API pour réveiller le backend (cold start ~10-15s sur Railway free tier).
  // Pendant que l'utilisateur lit la landing page ou tape ses identifiants,
  // le backend a le temps de démarrer → premières requêtes sans Network Error.
  useEffect(() => {
    const wakeup = async () => {
      try {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        if (!base) return;
        await fetch(`${base}/api/`, { method: "GET", credentials: "omit", mode: "cors" });
      } catch {
        // Silent — le retry interceptor gérera les vraies requêtes après
      }
    };
    wakeup();
  }, []);

  return (
    <div className="App">
      {/* React Query : fournisseur déplacé dans le cadre d'administration
          (components/layout/CadreAdmin.jsx), seul endroit où il sert. */}
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            {/* Filet de sécurité pleine page : « Recharger » pour toute page
                qui planterait. Les pages chargées à la demande ont en plus
                chacune la zone de leur groupe (Shell, PageCompte, PagePoche) :
                le repli s'affiche aussi quand on y arrive en naviguant. */}
            <ZoneChargement pleinePage>
              <AppRouter />
            </ZoneChargement>
            <InstallPrompt />
            <Toaster position="bottom-right" richColors closeButton />
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
